import React from 'react'
import { Line } from 'react-chartjs-2'
import { Chart as ChartJS, Interaction, registerables } from 'chart.js'
import { CrosshairPlugin, Interpolate } from 'chartjs-plugin-crosshair'
import PropTypes from 'prop-types'
import { linearRegression, linearRegressionLine } from 'simple-statistics'

const borderWidth = 2
const lastNMonths = 6

ChartJS.register(CrosshairPlugin)
ChartJS.register(...registerables)
Interaction.modes.interpolate = Interpolate

function customLegendClickHandler(e, legendItem, legend) {
  const index = legendItem.datasetIndex

  if (index % 2 !== 0) {
    return null
  } else {
    let ci = legend.chart
    let datasetMetas = [ci.getDatasetMeta(index), ci.getDatasetMeta(index + 1)]
    datasetMetas.forEach((meta) => {
      meta.hidden =
        meta.hidden === null ? !ci.data.datasets[index].hidden : null
    })
    ci.update()
  }
}

function preprocessData(data) {
  return data.map((line) => {
    // Edge case where usage in prev month is actually 0?
    return line.map((point) => {
      return point['y']
    })
  })
}

function generateProjection(lines, currMonth) {
  return lines.map((line) => {
    const lineUpTillCurrent = line.filter((value) => value !== null)

    const regressionCoords = lineUpTillCurrent.map((y, index) => [index, y])
    const lastNMonthsRegression = regressionCoords
      .filter((point, index) => index >= regressionCoords.length - lastNMonths)
      .map((point, index) => [index, point[1]])
    // function to get Y coordinates of projection line
    const regressionY = linearRegressionLine(
      linearRegression(lastNMonthsRegression)
    )

    return line.map((point, index) => {
      if (point === null) {
        return Math.floor(regressionY(index - (currMonth - lastNMonths)))
        /* Keep version without math.floor */
        // return regressionY(index - (currMonth - lastNMonths))
      } else if (index === currMonth) {
        return point
      } else {
        return null
      }
    })
  })
}

export default function ChartJsUsageChart(props) {
  const { data, calLabels } = props
  const lines = preprocessData(data)
  const [line1, line2, line3] = lines

  // August
  const currMonth = 7

  const [projected1, projected2, projected3] = generateProjection(
    lines,
    currMonth
  )

  const chartData = {
    labels: calLabels,
    datasets: [
      {
        label: 'Data',
        data: line1,
        backgroundColor: '#2a71d0', // blue
        borderColor: '#2a71d0', // blue
        borderWidth: borderWidth,
        tension: 0.3
      },
      {
        label: 'Data',
        data: projected1,
        backgroundColor: '#C1C1C1', // grey
        borderColor: '#C1C1C1', // grey
        borderDash: [5, 10],
        borderWidth: borderWidth
      },
      {
        label: 'Calls',
        data: line2,
        backgroundColor: '#50AF95', // green
        borderColor: '#50AF95', // green
        borderWidth: borderWidth,
        tension: 0.3
      },
      {
        label: 'Calls',
        data: projected2,
        backgroundColor: '#C1C1C1', // grey
        borderColor: '#C1C1C1', // grey
        borderDash: [5, 10],
        borderWidth: borderWidth
      },
      {
        label: 'SMS',
        data: line3,
        backgroundColor: '#f3ba2f', // yellow
        borderColor: '#f3ba2f', // yellow
        borderWidth: borderWidth,
        tension: 0.3
      },
      {
        label: 'SMS',
        data: projected3,
        backgroundColor: '#C1C1C1', // grey
        borderColor: '#C1C1C1', // grey
        borderDash: [5, 10],
        borderWidth: borderWidth
      }
    ]
  }

  return (
    <Line
      data={chartData}
      data-cy='lineChart'
      options={{
        scales: {
          y: {
            type: 'logarithmic',
            grid: {
              display: false
            }
          },
          x: {
            grid: {
              display: false
            }
          }
        },
        plugins: {
          tooltip: {
            width: 300,
            mode: 'nearest',
            intersect: false,
            filter: function (tooltipItem) {
              if (
                tooltipItem.datasetIndex % 2 === 1 &&
                tooltipItem.dataIndex === currMonth
              ) {
                return false
              } else {
                return true
              }
            },
            callbacks: {
              afterLabel: function (tooltipItem) {
                if (tooltipItem.datasetIndex <= 1) {
                  return 'MB'
                } else if (
                  tooltipItem.datasetIndex === 2 ||
                  tooltipItem.datasetIndex === 3
                ) {
                  return 'Mins'
                } else {
                  return 'Msgs'
                }
              }
            }
          },
          crosshair: {
            line: {
              color: '#C1C1C1', // grey
              width: 2
            },
            sync: {
              enabled: true, // enable trace line syncing with other charts
              group: 1, // chart group
              suppressTooltips: false // suppress tooltips when showing a synced tracer
            }
          },
          legend: {
            onClick: customLegendClickHandler,
            labels: {
              filter: function (legendItem) {
                return legendItem.datasetIndex % 2 === 1 ? false : true
              }
            }
          }
        },
        transitions: {
          active: {
            animation: {
              duration: 1000
            }
          }
        }
      }}
    />
  )
}

ChartJsUsageChart.propTypes = {
  data: PropTypes.array.isRequired,
  view: PropTypes.string.isRequired,
  calLabels: PropTypes.array.isRequired
}
